<script setup>
import { ChevronDown, Menu, User, X } from 'lucide-vue-next'
import { canSeeAnyDashboard } from '~/lib/utils';

const config = useRuntimeConfig()
const route = useRoute();
const user = useUser();
const cosmo = useCosmo();
const isMobileMenuOpen = ref(false)
let scrollRafId = null
const isScrolled = ref(false)
const isHome = computed(() => route.meta.layout === 'home')
const openMobileMenu = () => isMobileMenuOpen.value = true;
const canSeeMarketPlace = () => config?.public?.isMain && user.hasFeature('MARKET_PLACE');

const navigationItems = computed(() => [
  { href: '/search', label: 'Reserve' },
  { href: '/parking-packs', label: 'Season Passes', disabled: !globalConfig().hasPacks && !config?.public?.isMain },
  { href: '/market', label: 'Specials', disabled: !canSeeMarketPlace() },
  { href: '/owner-signup', label: 'List Your Spots', disabled: !config?.public?.isMain },
  { href: '/signup', label: 'Sign Up', disabled: user.getUser.id ? true : false },
  { href: '/login', label: 'Login', disabled: user.getUser.id ? true : false },
].filter((item) => !item.disabled));

const aboutUsNavigationItems = computed(() => [
  { href: '/investors', label: 'Investors'},
  { href: '/FAQs', label: 'FAQs' },
  { href: '/news-happenings', label: 'News', disabled: !config?.public?.isMain },
  { href: '/contact-us', label: 'Contact Us' },
  { href: '/about', label: 'Company' },
].filter((item) => !item.disabled));

const userNavigationLinks = computed(() => [
  { href: '/dashboard', label: 'My Reservations', disabled: !canSeeAnyDashboard(user.getUser?.role) },
  { href: '/admin', label: 'Admin Dashboard', disabled: !user.getUser?.is_staff },
  { href: '/dashboard/wallet', label: 'My Wallet', disabled: user.getUser?.role === 'PARKER' ? false : true },
  { href: '/dashboard/settings', label: 'Settings' },
].filter((item) => !item.disabled));

const handleScroll = () => {
  if (scrollRafId) return
  scrollRafId = requestAnimationFrame(() => {
    isScrolled.value = window.scrollY > 50
    scrollRafId = null
  })
}

const setupScrollListener = () => {
  if (isHome.value) {
    window.addEventListener('scroll', handleScroll, { passive: true })
    isScrolled.value = window.scrollY > 50
  } else {
    window.removeEventListener('scroll', handleScroll)
    isScrolled.value = false
  }
}

watch(isHome, () => {
  setupScrollListener()
});

onMounted(() => {
  setupScrollListener()
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  if (scrollRafId) {
    cancelAnimationFrame(scrollRafId)
    scrollRafId = null
  }
});
</script>

<template>
  <nav
    class="fixed z-30 top-0"
    :class="{
      'bg-primary transition-[background] delay-100': !isHome || isScrolled,
    }"
    :style="{
      width: cosmo.isOpen ? 'calc(100vw - 400px)' : '100vw'
    }"
  >
    <div class="mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-16">
        <!-- Logo and Site Title -->
        <NuxtLink class="flex items-center" to="/" v-if="config?.public?.isMain">
          <NuxtImg class="h-12 w-12 mr-2" src="/images/spotsurferLogo.png" alt="Logo for Spotsurfer" />
          <div class="flex flex-col">
            <span
              class="text-3xl font-semibold uppercase"
              :class="{
                'text-white': !isHome || isScrolled,
                'text-primary': isHome && !isScrolled 
              }"
            >
              {{ config?.public?.siteName }}
            </span>
            <span
              class="text-xs tracking-widest uppercase"
              :class="{
                'text-white': !isHome || isScrolled,
                'text-primary': isHome && !isScrolled 
              }"
            >
              Reserve. Park. Play.
            </span>
          </div>
        </NuxtLink>
        <NuxtLink class="flex items-center" to="/" v-else>
          <NuxtImg
            class="max-h-16 py-2 w-auto object-cover"
            style="min-width: 100px; max-width: 200px;"
            :src="globalConfig()?.logo?.url"
            :alt="`${globalConfig()?.logo?.name}`"
          />
          <span class="text-xl text-nav-link-default font-semibold uppercase tracking-wider px-2">- parking</span>
        </NuxtLink>
        <!-- Navigation Links (Desktop) -->
        <div class="hidden lg:block">
          <div class="flex items-center gap-6">
            <NavigationMenu>
              <NavigationMenuList class="gap-6">
                <NavigationMenuItem v-for="item in navigationItems" :key="item.href">
                  <NuxtLink
                    :to="item.href"
                    class="transition-colors hover:text-nav-link-hover text-nav-link-default text-base"
                    :class="{
                      'text-white': !isHome || isScrolled,
                      'text-primary': isHome && !isScrolled 
                    }"
                  >
                    {{ item.label }}
                  </NuxtLink>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
            <DropdownMenu v-if="config?.public?.isMain">
              <DropdownMenuTrigger
                class="flex items-center text-nav-link-default hover:text-nav-link-hover"
                :class="{
                  'text-white': !isHome || isScrolled,
                  'text-primary': isHome && !isScrolled 
                }"
              >
                About Us
                <ChevronDown class="ml-1" :size="20" />
              </DropdownMenuTrigger>
              <DropdownMenuContent class="border-none p-0">
                <DropdownMenuItem class="p-0" v-for="item in aboutUsNavigationItems" :key="item.label">
                  <NuxtLink class="w-full p-2" :to="item.href">{{ item.label }}</NuxtLink>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger class="text-nav-link-default hover:text-nav-link-hover">
                <span class="flex items-center" v-if="user.getUser?.avatar">
                  <NuxtImg class="h-8 w-8 rounded-full object-cover border-2" :src="user.getUser?.avatar" />
                  <ChevronDown
                    class="ml-1"
                    :class="{
                      'text-white': !isHome || isScrolled,
                      'text-primary': isHome && !isScrolled
                    }"
                    :size="18"
                  />
                </span>
                <div class="flex items-center" v-else-if="user.getUser?.id">
                  <User
                    :class="{
                      'text-white': !isHome || isScrolled,
                      'text-primary': isHome && !isScrolled
                    }"
                  />
                  <ChevronDown
                    class="ml-1"
                    :class="{
                      'text-white': !isHome || isScrolled,
                      'text-primary': isHome && !isScrolled
                    }"
                    :size="18"
                  />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent class="border-none p-0">
                <DropdownMenuItem class="p-0" v-for="item in userNavigationLinks" :key="item.label">
                  <NuxtLink class="w-full p-2" :to="item.href">{{ item.label }}</NuxtLink>
                </DropdownMenuItem>
                <DropdownMenuItem><NuxtLink class="cursor-pointer w-full" @click="useLogout">Logout</NuxtLink></DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <!-- Mobile menu button -->
        <div class="lg:hidden">
          <button @click="openMobileMenu">
            <Menu
              :size="32"
              class="hover:stroke-nav-link-hover"
              :class="{
                'text-white': !isHome || isScrolled,
                'text-primary': isHome && !isScrolled 
              }"
            />
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile Flyout Menu -->
    <Sheet v-model:open="isMobileMenuOpen" side="right">
      <SheetContent class="w-full md:w-1/2 bg-primary text-white shadow-lg p-0 border-none">
        <SheetClose class="absolute right-2 top-2 bg-primary text-white z-50 rounded-full">
          <X />
        </SheetClose>
        <SheetHeader class="sr-only">
          <SheetTitle class="text-lg font-bold">Menu</SheetTitle>
        </SheetHeader>
        <SheetDescription class="sr-only">Main application menu for Spotsurfer</SheetDescription>
        <nav class="flex flex-col mt-4">
          <NuxtLink class="flex items-center" to="/">
            <template v-if="config?.public?.isMain">
              <NuxtImg class="h-8 w-8 mx-2" src="/images/spotsurferLogo.png" alt="Spotsurfer Logo" />
              <div class="flex flex-col">
                <span class="text-3xl text-white font-semibold uppercase">{{ config?.public?.siteName }}</span>
                <span class="text-white text-xs tracking-widest uppercase">Reserve. Park. Play.</span>
              </div>
            </template>
            <template v-else>
              <NuxtLink class="flex items-center" to="/">
                <NuxtImg
                  class="max-h-16 p-2 w-auto object-contain"
                  style="min-width: 80px; max-width: 200px;"
                  :src="globalConfig()?.logo?.url"
                  :alt="`${globalConfig()?.logo?.name}`"
                />
              </NuxtLink>
            </template>
          </NuxtLink>
          <SheetClose class="mt-4" asChild>
            <NuxtLink
              class="flex flex-col text-white p-2 hover:underline transition-colors text-nav-link-default hover:text-nav-link-hover"
              v-for="item in navigationItems"
              :key="item.href"
              :to="item.href"
              @click="isMobileMenuOpen = false"
            >
              {{ item.label }}
            </NuxtLink>
            <div class="flex flex-col" v-if="user.getUser?.id">
              <hr />
              <NuxtLink
                class="text-white cursor-pointer w-full text-nav-link-default hover:text-nav-link-hover hover:underline p-2"
                v-for="item in userNavigationLinks"
                :key="item.label"
                :to="item.href"
                @click="isMobileMenuOpen = false"
              >
                {{ item.label }}
              </NuxtLink>
            </div>
            <div class="flex flex-col" v-if="config?.public?.isMain">
              <hr />
              <NuxtLink
                class="text-white cursor-pointer w-full text-nav-link-default hover:text-nav-link-hover hover:underline p-2"
                v-for="item in aboutUsNavigationItems"
                :key="item.label"
                :to="item.href"
                @click="isMobileMenuOpen = false"
              >
                {{ item.label }}
              </NuxtLink>
            </div>
            <NuxtLink class="cursor-pointer w-full text-nav-link-default hover:text-nav-link-hover hover:underline p-2" @click="useLogout" v-if="user.getUser?.id">Logout</NuxtLink>
          </SheetClose>
        </nav>
      </SheetContent>
    </Sheet>
  </nav>
</template>
